import { z } from 'zod';
import { DashboardTileTypes } from '../../types/dashboard';
import { AsCodeInternalization } from './abstract';
import { mergeExisting } from './merge';
const dashboardAsCodeSchema = z.object({
    name: z.string(),
    description: z
        .string()
        .nullable()
        .optional()
        .transform((str) => str ?? undefined),
    tiles: z.array(z.union([
        z.object({
            type: z.union([
                z.literal(DashboardTileTypes.SAVED_CHART),
                z.literal(DashboardTileTypes.SQL_CHART),
            ]),
            properties: z.object({
                title: z.string(),
                chartName: z.string().optional().default(''),
            }),
        }),
        z.object({
            type: z.literal(DashboardTileTypes.MARKDOWN),
            properties: z.object({
                title: z.string(),
                content: z.string(),
            }),
        }),
        z.object({
            type: z.literal(DashboardTileTypes.LOOM),
            properties: z.object({
                title: z.string(),
            }),
        }),
        z.object({
            type: z.literal(DashboardTileTypes.HEADING),
            properties: z.object({
                text: z.string(),
            }),
        }),
        z.object({
            type: z.literal(DashboardTileTypes.DATA_APP),
            properties: z.object({
                title: z.string(),
            }),
        }),
    ])),
});
export const getFilterLabelTranslation = (labels, sourceLabel) => {
    if (!sourceLabel)
        return undefined;
    const translated = labels[sourceLabel];
    return typeof translated === 'string' && translated.length > 0
        ? translated
        : undefined;
};
export const translateFilterRuleLabels = (rules, labels) => rules.map((rule) => {
    const translated = getFilterLabelTranslation(labels, rule.label);
    return translated ? { ...rule, label: translated } : rule;
});
const getFilterLabels = (filters) => {
    const labels = [
        ...(filters?.dimensions ?? []),
        ...(filters?.metrics ?? []),
        ...(filters?.tableCalculations ?? []),
    ]
        .map((rule) => rule.label)
        .filter((label) => typeof label === 'string' && label.length > 0);
    return labels.length > 0
        ? Object.fromEntries(labels.map((label) => [label, label]))
        : undefined;
};
export class DashboardAsCodeInternalization extends AsCodeInternalization {
    constructor(schema = dashboardAsCodeSchema) {
        super();
        this.schema = schema;
    }
    getLanguageMap(dashboardAsCode) {
        const filterLabels = getFilterLabels(dashboardAsCode.filters);
        return {
            dashboard: {
                [dashboardAsCode.slug]: {
                    ...this.schema.partial().strip().parse(dashboardAsCode),
                    filters: filterLabels
                        ? { labels: filterLabels }
                        : undefined,
                },
            },
        };
    }
    // eslint-disable-next-line class-methods-use-this
    merge(internalizationMap, content) {
        const { filters: filterOverrides, ...contentOverrides } = internalizationMap;
        const merged = mergeExisting(content, contentOverrides);
        const labels = filterOverrides?.labels;
        if (labels && merged.filters) {
            merged.filters = {
                ...merged.filters,
                dimensions: translateFilterRuleLabels(merged.filters.dimensions ?? [], labels),
                metrics: translateFilterRuleLabels(merged.filters.metrics ?? [], labels),
                tableCalculations: translateFilterRuleLabels(merged.filters.tableCalculations ?? [], labels),
            };
        }
        return merged;
    }
}
//# sourceMappingURL=dashboardAsCode.js.map